import React, { useEffect, useMemo, useState } from 'react';
import { Form, FormikProvider, useFormik } from 'formik';
import { useDispatch, useSelector } from 'react-redux';
import { useSnackbar } from 'notistack';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  FormHelperText,
  FormLabel,
  Grid,
  InputLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  Stack,
  Typography,
} from '@mui/material';
import GlobalDialog from '../../../../components/mui/Dialogue/GlobalDialog';
import {
  changeCourse,
  clearCourseChangeBatches,
  getCourseChangeBatches,
  getCourseChangeCourses,
} from '../../../../redux/features/CRDslice';

/**
 * CRM Course Change wizard.
 *
 * @param {object}   props.enrollment  Normalised current-enrollment summary (see SummaryCard fields).
 * @param {boolean}  props.open
 * @param {function} props.setOpen
 * @param {function} props.onSuccess   Called after the user closes the success dialog (refresh the parent).
 */
export default function ChangeCourseModal({ enrollment, open, setOpen, onSuccess }) {
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const { courseChange } = useSelector((state) => state.crd);
  const { coursesCache = {}, batches = [], submitting, loading } = courseChange || {};

  const [apiError, setApiError] = useState('');
  const [successInfo, setSuccessInfo] = useState(null); // { message, redirectUrl }

  const totalSessions = Number(enrollment?.totalSessions) || 0;
  const completedSessions = Number(enrollment?.completedSessions) || 0;
  const defaultRemaining = Math.max(totalSessions - completedSessions, 0);

  // Plain Formik validator (no Yup) to avoid version-specific schema quirks.
  const validate = (values) => {
    const errors = {};
    if (!values.deliveryType) errors.deliveryType = 'Select a delivery model';
    if (!values.targetCourseId) errors.targetCourseId = 'Select a target course';
    if (values.deliveryType === 'batch' && !values.targetBatchId) {
      errors.targetBatchId = 'Select a target batch';
    }
    return errors;
  };

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      deliveryType: 'batch',
      targetCourseId: '',
      targetBatchId: '',
      selectedSubjects: [],
      keepSameCro: false,
      keepSameTutor: false,
      keepSamePreference: false,
      remainingSessions: defaultRemaining,
    },
    validate,
    onSubmit: (values) => {
      setApiError('');

      // Capacity guard: block submission if the chosen batch is full.
      if (values.deliveryType === 'batch') {
        const batch = batches.find((b) => String(b.courseBatchID) === String(values.targetBatchId));
        if (batch && Number(batch.availableSeats) <= 0) {
          setApiError('The selected batch is full. Please choose a batch with available seats.');
          return;
        }
      }

      const payload = {
        courseEnrollmentId: Number(enrollment?.courseEnrollmentId),
        targetCourseId: Number(values.targetCourseId),
        deliveryType: values.deliveryType,
        targetBatchId: values.deliveryType === 'batch' ? Number(values.targetBatchId) : null,
        selectedSubjects: [],
        keepSameCro: Boolean(values.keepSameCro),
        keepSameTutor: values.deliveryType === 'single' ? Boolean(values.keepSameTutor) : false,
        keepSamePreference: values.deliveryType === 'single' ? Boolean(values.keepSamePreference) : false,
        remainingSessions: Number(values.remainingSessions),
      };

      dispatch(changeCourse(payload)).then((res) => {
        const data = res?.payload;
        if (data?.status === 'success') {
          setSuccessInfo({
            message: data?.message,
            redirectUrl: data?.data?.redirectUrl || data?.redirectUrl || '',
          });
        } else {
          const message = data?.message || 'Failed to change course. Please try again.';
          setApiError(message);
          enqueueSnackbar(message, { variant: 'error' });
        }
      });
    },
  });

  const isBatch = formik.values.deliveryType === 'batch';

  // Target courses depend on the delivery model: Group (Batch) => batch_availability 1, Single (1-1) => 0.
  const wantAvailability = isBatch ? 1 : 0;
  const courses = coursesCache[wantAvailability] || [];
  // The course dropdown is only "loading" while the list for the chosen model isn't cached yet.
  const coursesLoading = loading && !coursesCache[wantAvailability];

  // Reset modal state when it closes.
  useEffect(() => {
    if (!open) {
      setApiError('');
      setSuccessInfo(null);
      formik.resetForm();
      dispatch(clearCourseChangeBatches());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Fetch target courses for the selected delivery model — only if not already cached.
  useEffect(() => {
    if (open && !coursesCache[wantAvailability]) {
      dispatch(getCourseChangeCourses(wantAvailability));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, wantAvailability]);

  const handleCourseChange = (courseAvailableId) => {
    formik.setFieldValue('targetCourseId', courseAvailableId);
    formik.setFieldValue('targetBatchId', '');
    if (courseAvailableId) {
      dispatch(getCourseChangeBatches(courseAvailableId));
    } else {
      dispatch(clearCourseChangeBatches());
    }
  };

  const selectedBatch = useMemo(
    () => batches.find((b) => String(b.courseBatchID) === String(formik.values.targetBatchId)),
    [batches, formik.values.targetBatchId],
  );
  const selectedBatchFull = selectedBatch && Number(selectedBatch.availableSeats) <= 0;

  const handleAddFeeTransfer = () => {
    if (successInfo?.redirectUrl) {
      window.open(successInfo.redirectUrl, '_blank', 'noopener,noreferrer');
    }
  };

  const handleSuccessClose = () => {
    setSuccessInfo(null);
    setOpen(false);
    if (onSuccess) onSuccess();
  };

  return (
    <>
      <GlobalDialog
        title="Migrate Course Enrollment"
        open={open}
        setOpen={setOpen}
        fullWidth
        maxWidth="md"
        closeButton
        content={
          <FormikProvider value={formik}>
            <Form onSubmit={formik.handleSubmit}>
              {apiError ? (
                <Alert severity="error" sx={{ mb: 2 }} onClose={() => setApiError('')}>
                  {apiError}
                </Alert>
              ) : null}

              <Grid container spacing={3}>
                {/* ---------------- Current status ---------------- */}
                <Grid item xs={12} md={6}>
                  <Box sx={{ p: 2, bgcolor: 'grey.100', borderRadius: 2, height: '100%' }}>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                      CURRENT STATUS
                    </Typography>
                    <Stack spacing={0.5}>
                      <Typography variant="body2">
                        <b>Student:</b> {enrollment?.studentName || '-'} (Admn: {enrollment?.admissionNo || '-'})
                      </Typography>
                      <Typography variant="body2">
                        <b>Active Course:</b> {enrollment?.courseName || '-'} ({enrollment?.deliveryModel || '-'})
                      </Typography>
                      <Typography variant="body2">
                        <b>Completed Sessions:</b> {completedSessions} / {totalSessions}
                      </Typography>
                      <Typography variant="body2">
                        <b>Assigned Tutor:</b> {enrollment?.tutorName || '-'}
                      </Typography>
                      <Typography variant="body2">
                        <b>Assigned CRO:</b> {enrollment?.croName || '-'}
                      </Typography>
                    </Stack>
                  </Box>
                </Grid>

                {/* ---------------- Target configuration ---------------- */}
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    TARGET CONFIGURATION
                  </Typography>

                  <FormControl component="fieldset" sx={{ mb: 2 }}>
                    <FormLabel component="legend" sx={{ fontSize: '0.85rem' }}>
                      Target Delivery Model
                    </FormLabel>
                    <RadioGroup
                      row
                      name="deliveryType"
                      value={formik.values.deliveryType}
                      onChange={(e) => {
                        // Update all dependent fields in a single render to avoid switch lag.
                        // Course list differs per delivery model — reset selections when toggling.
                        formik.setValues(
                          {
                            ...formik.values,
                            deliveryType: e.target.value,
                            targetCourseId: '',
                            targetBatchId: '',
                            selectedSubjects: [],
                          },
                          false,
                        );
                        dispatch(clearCourseChangeBatches());
                      }}
                    >
                      <FormControlLabel value="single" control={<Radio />} label="Single (1-1)" />
                      <FormControlLabel value="batch" control={<Radio />} label="Group (Batch)" />
                    </RadioGroup>
                  </FormControl>

                  <FormControl
                    fullWidth
                    sx={{ mb: 2 }}
                    error={formik.touched.targetCourseId && Boolean(formik.errors.targetCourseId)}
                  >
                    <InputLabel id="target-course-label">
                      {coursesLoading ? 'Loading courses…' : 'Target Course'}
                    </InputLabel>
                    <Select
                      labelId="target-course-label"
                      label={coursesLoading ? 'Loading courses…' : 'Target Course'}
                      value={coursesLoading ? '' : formik.values.targetCourseId}
                      onChange={(e) => handleCourseChange(e.target.value)}
                      disabled={coursesLoading}
                      endAdornment={
                        coursesLoading ? (
                          <CircularProgress size={18} sx={{ mr: 3 }} color="inherit" />
                        ) : null
                      }
                    >
                      {courses.map((course) => (
                        <MenuItem key={course.course_available_id} value={course.course_available_id}>
                          {course.course_name}
                        </MenuItem>
                      ))}
                    </Select>
                    {formik.touched.targetCourseId && formik.errors.targetCourseId ? (
                      <FormHelperText>{formik.errors.targetCourseId}</FormHelperText>
                    ) : null}
                  </FormControl>

                  {/* Batch branch */}
                  {isBatch ? (
                    <>
                      <FormControl
                        fullWidth
                        sx={{ mb: 1 }}
                        error={formik.touched.targetBatchId && Boolean(formik.errors.targetBatchId)}
                      >
                        <InputLabel id="target-batch-label">Target Batch</InputLabel>
                        <Select
                          labelId="target-batch-label"
                          label="Target Batch"
                          value={formik.values.targetBatchId}
                          onChange={(e) => formik.setFieldValue('targetBatchId', e.target.value)}
                          disabled={!formik.values.targetCourseId}
                        >
                          {batches.map((batch) => {
                            const full = Number(batch.availableSeats) <= 0;
                            return (
                              <MenuItem key={batch.courseBatchID} value={batch.courseBatchID} disabled={full}>
                                {batch.display} ({batch.usedSeats}/{batch.course_batch_capacity})
                                {full ? ' — Full' : ''}
                              </MenuItem>
                            );
                          })}
                        </Select>
                        {formik.touched.targetBatchId && formik.errors.targetBatchId ? (
                          <FormHelperText>{formik.errors.targetBatchId}</FormHelperText>
                        ) : null}
                      </FormControl>
                      <Alert severity="info" sx={{ mb: 1 }}>
                        Tutors, subjects, and schedules will be inherited automatically from this batch definition.
                      </Alert>
                    </>
                  ) : null}

                  {/* Single (1-1) branch */}
                  {!isBatch ? (
                    <Box sx={{ mb: 1 }}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={formik.values.keepSameTutor}
                            onChange={(e) => formik.setFieldValue('keepSameTutor', e.target.checked)}
                          />
                        }
                        label="Retain Same Trainer/Tutor for matching subjects?"
                        sx={{ display: 'block' }}
                      />
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={formik.values.keepSamePreference}
                            onChange={(e) => formik.setFieldValue('keepSamePreference', e.target.checked)}
                          />
                        }
                        label="Keep same schedule preferences (times & days)?"
                        sx={{ display: 'block' }}
                      />
                    </Box>
                  ) : null}
                </Grid>
              </Grid>

              <Divider sx={{ my: 2 }} />

              {/* ---------------- Resource retention ---------------- */}
              <Box>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  RESOURCE RETENTION
                </Typography>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formik.values.keepSameCro}
                      onChange={(e) => formik.setFieldValue('keepSameCro', e.target.checked)}
                    />
                  }
                  label={`Retain Same CRO${enrollment?.croName ? ` (${enrollment.croName})` : ''}`}
                  sx={{ display: 'block' }}
                />
              </Box>

              <Stack direction="row" spacing={1} justifyContent="flex-end" sx={{ mt: 3 }}>
                <Button variant="outlined" color="primary" onClick={() => setOpen(false)} disabled={submitting}>
                  Cancel
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  disabled={submitting || selectedBatchFull}
                  startIcon={submitting ? <CircularProgress size={16} color="inherit" /> : null}
                >
                  {submitting ? 'Please wait…' : 'Confirm & Apply'}
                </Button>
              </Stack>
            </Form>
          </FormikProvider>
        }
      />

      {/* ---------------- Success dialog ---------------- */}
      <Dialog open={Boolean(successInfo)} onClose={handleSuccessClose} maxWidth="xs" fullWidth>
        <DialogTitle>Course Switched</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {successInfo?.message ||
              'Course switched successfully! Do you want to perform a Fee Transfer for this student?'}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleSuccessClose} color="inherit">
            Close
          </Button>
          <Button onClick={handleAddFeeTransfer} variant="contained" disabled={!successInfo?.redirectUrl}>
            Add Fee Transfer
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
